"use client";

import { useTranslations } from "@i18n/react";
import type { Cart } from "@packages/sdk";
import { useEffect, useState } from "react";

import { useLineItemFormatter } from "../../../hooks/useLineItemFormatter";
import { useTrialType } from "../../../hooks/useTrialType";
import { Text } from "../../_base/_core/Text/Text";
import {
  CheckoutLineItem,
  CheckoutLineItemContainer,
} from "../checkout/CheckoutLineItem";
import { costPerMonth } from "./costPerMonth";

type CartTotalProps = Pick<
  Cart,
  | "total"
  | "renewal_total"
  | "product"
  | "currency"
  | "trial_end_date"
  | "intro_offer"
  | "payment_required"
  | "pretax_total"
  | "renewal_pretax_total"
>;

export const CartTotal = ({
  total,
  renewal_total,
  product,
  currency,
  trial_end_date,
  intro_offer,
  payment_required,
  pretax_total,
  renewal_pretax_total,
}: CartTotalProps) => {
  const t = useTranslations();
  const formatter = useLineItemFormatter({ currency });
  const trialType = useTrialType({ trial_end_date, intro_offer });

  const [isAustraliaOnboarding, setIsAustraliaOnboarding] = useState(false);

  useEffect(() => {
    if (currency === "AUD" && window.location.href.includes("onboarding"))
      setIsAustraliaOnboarding(true);
    else setIsAustraliaOnboarding(false);
  }, [currency]);

  return (
    <CheckoutLineItemContainer>
      <CheckoutLineItem
        label={t("total_due_today")}
        value={formatter.format(
          trialType === "paid"
            ? intro_offer.amount
            : trialType === "free"
              ? 0
              : total,
        )}
        currency={currency}
        bold
      />
      {product.external_id ===
      "lifetime_full_subscription" ? null : trialType === "none" ? (
        <CheckoutLineItem
          label={t("general_words_cost_per_month")}
          value={formatter.format(
            costPerMonth({
              product,
              renewal_total: isAustraliaOnboarding ? pretax_total : total,
            }),
          )}
          currency={currency}
        />
      ) : payment_required ? (
        <>
          <CheckoutLineItem
            label={t("cost_per_month")}
            value={formatter.format(
              costPerMonth({
                product,
                renewal_total:
                  trialType === "paid"
                    ? isAustraliaOnboarding
                      ? renewal_pretax_total
                      : renewal_total
                    : isAustraliaOnboarding
                      ? pretax_total
                      : total,
              }),
            )}
            currency={currency}
          />
          <Text size={"xl"} type={"detail"} as={"p"}>
            {isAustraliaOnboarding
              ? t("web_checkout_billed_after_trial_no_taxes", {
                  "0": formatter.format(
                    trialType === "paid" ? renewal_pretax_total : pretax_total,
                  ),
                })
              : t("web_checkout_billed_after_trial", {
                  "0": formatter.format(
                    trialType === "paid" ? renewal_total : total,
                  ),
                })}
          </Text>
        </>
      ) : null}
    </CheckoutLineItemContainer>
  );
};
